import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import { MoreVert } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { colors } from "../../theme/colors";
import { PROFILE_TABS } from "./profileTabConfig";
import { MOBILE_NAV_MEDIA } from "../layout/layoutConstants";

const ACTIVE_TAB_BACKGROUND = `
    linear-gradient(
        180deg,
        rgba(255,255,255,0.055) 0%,
        rgba(255,255,255,0.025) 100%
    )
`;

const PROFILE_TABS_OVERFLOW_BELOW_PX = 1300;
const MOBILE_PRIMARY_TAB_IDS = ["overview", "matches", "performance"];
const TAB_ROW_GAP_PX = 8;
const TAB_ROW_GAP_MOBILE_PX = 4;
const MORE_BUTTON_SIZE = "2.25rem";

function isMobileViewport() {
    return typeof window !== "undefined" && window.matchMedia(MOBILE_NAV_MEDIA).matches;
}

function getRowGapPx() {
    return isMobileViewport() ? TAB_ROW_GAP_MOBILE_PX : TAB_ROW_GAP_PX;
}

function getInitialOverflowTabs(tabs) {
    if (typeof window === "undefined") {
        return [];
    }

    if (window.innerWidth >= PROFILE_TABS_OVERFLOW_BELOW_PX) {
        return [];
    }

    if (isMobileViewport()) {
        return tabs.filter(
            (tab) => !MOBILE_PRIMARY_TAB_IDS.includes(tab.id)
        );
    }

    return tabs.filter((tab) => tab.id === "loadout");
}

function sameTabs(left, right) {
    return (
        left.length === right.length &&
        left.every((tab, index) => tab.id === right[index].id)
    );
}

function ProfileTab({ label, isActive, onClick, ...rest }) {
    return (
        <Box
            role="tab"
            aria-selected={isActive}
            component="button"
            type="button"
            onClick={onClick}
            {...rest}
            sx={{
                position: "relative",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                minHeight: "2.35rem",
                px: { xs: "0.42rem", sm: "0.7rem", md: "0.95rem" },
                py: { xs: "0.5rem", sm: "0.55rem", md: "0.6rem" },
                m: 0,
                border: isActive
                    ? `1px solid ${alpha(colors.accent.main, 0.14)}`
                    : "1px solid transparent",
                borderRadius: "9px",
                background: isActive ? ACTIVE_TAB_BACKGROUND : "transparent",
                boxShadow: isActive
                    ? "inset 0 1px 0 rgba(255,255,255,0.045), 0 4px 14px rgba(0,0,0,0.16)"
                    : "none",
                cursor: "pointer",
                font: "inherit",
                boxSizing: "border-box",
                outline: 0,
                transition:
                    "background-color 120ms ease-out, border-color 120ms ease-out, box-shadow 120ms ease-out",
                "&:hover": {
                    background: isActive
                        ? "linear-gradient(180deg, rgba(255,255,255,0.065), rgba(255,255,255,0.03))"
                        : "rgba(255,255,255,0.032)",
                },
                "&:hover .MuiTypography-root": {
                    color: isActive
                        ? colors.text.primary
                        : colors.text.secondary,
                },
                "&:focus-visible": {
                    borderColor: alpha(colors.accent.soft, 0.52),
                    boxShadow: `0 0 0 3px ${alpha(colors.primary.main, 0.12)}`,
                },
                "&::after": {
                    content: '""',
                    position: "absolute",
                    left: "50%",
                    bottom: "0.22rem",
                    width: isActive ? "1.1rem" : 0,
                    height: "1px",
                    borderRadius: "999px",
                    background: isActive
                        ? `linear-gradient(90deg, transparent, ${colors.accent.soft}, transparent)`
                        : "transparent",
                    transform: "translateX(-50%)",
                    opacity: isActive ? 0.9 : 0,
                    pointerEvents: "none",
                    transition: "width 120ms ease-out, opacity 100ms ease-out",
                },
            }}
        >
            <Typography
                sx={{
                    position: "relative",
                    zIndex: 1,
                    fontSize: { xs: "0.6rem", sm: "0.68rem", md: "0.78rem" },
                    fontWeight: isActive ? 680 : 560,
                    letterSpacing: { xs: "0.075em", sm: "0.095em", md: "0.105em" },
                    textTransform: "uppercase",
                    color: isActive ? colors.text.primary : colors.text.muted,
                    transition: "color 110ms ease-out, opacity 110ms ease-out",
                    whiteSpace: "nowrap",
                }}
            >
                {label}
            </Typography>
        </Box>
    );
}

ProfileTab.propTypes = {
    label: PropTypes.string.isRequired,
    isActive: PropTypes.bool.isRequired,
    onClick: PropTypes.func.isRequired,
};

export default function ProfileTabBar({ value, onChange, tabs = PROFILE_TABS }) {
    const rowRef = useRef(null);
    const measureRef = useRef(null);
    const moreMeasureRef = useRef(null);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [overflowTabs, setOverflowTabs] = useState(() => getInitialOverflowTabs(tabs));
    const menuOpen = Boolean(menuAnchor);
    const overflowActive = overflowTabs.some((tab) => tab.id === value);
    const visibleTabs = tabs.filter(
        (tab) => !overflowTabs.some((overflowTab) => overflowTab.id === tab.id)
    );

    const updateOverflow = useCallback(() => {
        const row = rowRef.current;
        const measure = measureRef.current;
        if (!row || !measure) return;

        const tabNodes = Array.from(
            measure.querySelectorAll("[data-measure-tab]")
        );
        if (tabNodes.length !== tabs.length) return;

        const widths = tabNodes.map((node) => node.offsetWidth);
        const rowStyles = window.getComputedStyle(row);
        const available =
            row.clientWidth -
            (parseFloat(rowStyles.paddingLeft) || 0) -
            (parseFloat(rowStyles.paddingRight) || 0);
        const gap = getRowGapPx();
        const moreWidth = moreMeasureRef.current?.offsetWidth || 36;
        const minVisible = isMobileViewport()
            ? MOBILE_PRIMARY_TAB_IDS.length
            : 1;
        const totalWidth =
            widths.reduce((sum, width) => sum + width, 0) +
            gap * Math.max(0, widths.length - 1);

        if (totalWidth <= available) {
            setOverflowTabs((prev) => (prev.length === 0 ? prev : []));
            return;
        }

        const budget = Math.max(0, available - moreWidth - gap);
        let used = 0;
        let visibleCount = 0;

        for (let index = 0; index < widths.length; index += 1) {
            const next = used + (visibleCount > 0 ? gap : 0) + widths[index];
            if (next <= budget) {
                used = next;
                visibleCount += 1;
            } else {
                break;
            }
        }

        visibleCount = Math.max(
            minVisible,
            Math.min(visibleCount, tabs.length - 1)
        );
        const nextOverflow = tabs.slice(visibleCount);
        setOverflowTabs((prev) => (sameTabs(prev, nextOverflow) ? prev : nextOverflow));
    }, [tabs]);

    useEffect(() => {
        updateOverflow();

        const row = rowRef.current;
        if (!row) return undefined;

        window.addEventListener("resize", updateOverflow);

        if (typeof ResizeObserver === "undefined") {
            return () => window.removeEventListener("resize", updateOverflow);
        }

        const observer = new ResizeObserver(updateOverflow);
        observer.observe(row);
        return () => {
            observer.disconnect();
            window.removeEventListener("resize", updateOverflow);
        };
    }, [updateOverflow]);

    const handleOpenMenu = (event) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setMenuAnchor(null);
    };

    React.useEffect(() => {
        if (!menuOpen) return undefined;

        const closeOnScroll = () => {
            setMenuAnchor(null);
        };

        window.addEventListener("scroll", closeOnScroll, true);
        return () => {
            window.removeEventListener("scroll", closeOnScroll, true);
        };
    }, [menuOpen]);

    const handleOverflowSelect = (tabId) => {
        onChange?.(tabId);
        handleCloseMenu();
    };

    return (
        <Box
            sx={{
                position: "relative",
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                overflow: "visible",
            }}
        >
            <Box
                ref={measureRef}
                aria-hidden
                sx={{
                    position: "absolute",
                    visibility: "hidden",
                    pointerEvents: "none",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    flexWrap: "nowrap",
                    gap: `${TAB_ROW_GAP_PX}px`,
                    whiteSpace: "nowrap",
                    [`@media ${MOBILE_NAV_MEDIA}`]: {
                        gap: `${TAB_ROW_GAP_MOBILE_PX}px`,
                    },
                }}
            >
                {tabs.map((tab) => (
                    <ProfileTab
                        key={tab.id}
                        data-measure-tab=""
                        label={tab.label}
                        isActive={false}
                        onClick={() => {}}
                        tabIndex={-1}
                    />
                ))}
                <Box
                    ref={moreMeasureRef}
                    sx={{
                        width: MORE_BUTTON_SIZE,
                        height: MORE_BUTTON_SIZE,
                        flexShrink: 0,
                    }}
                />
            </Box>

            <Box
                ref={rowRef}
                role="tablist"
                aria-label="Profile sections"
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    flexWrap: "nowrap",
                    gap: `${TAB_ROW_GAP_PX}px`,
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,
                    mt: 0,
                    padding: 0,
                    background: "transparent",
                    overflow: "visible",
                    [`@media ${MOBILE_NAV_MEDIA}`]: {
                        gap: `${TAB_ROW_GAP_MOBILE_PX}px`,
                    },
                }}
            >
                {visibleTabs.map((tab) => (
                    <ProfileTab
                        key={tab.id}
                        label={tab.label}
                        isActive={value === tab.id}
                        onClick={() => onChange?.(tab.id)}
                    />
                ))}

                {overflowTabs.length > 0 ? (
                    <IconButton
                        aria-label="More profile sections"
                        aria-haspopup="true"
                        aria-expanded={menuOpen ? "true" : undefined}
                        aria-controls={menuOpen ? "profile-tab-overflow-menu" : undefined}
                        onClick={handleOpenMenu}
                        sx={{
                            width: MORE_BUTTON_SIZE,
                            height: MORE_BUTTON_SIZE,
                            flexShrink: 0,
                            ml: "auto",
                            borderRadius: "8px",
                            border: overflowActive
                                ? `1px solid ${alpha(colors.accent.main, 0.14)}`
                                : "1px solid transparent",
                            background: overflowActive
                                ? ACTIVE_TAB_BACKGROUND
                                : "transparent",
                            color: overflowActive
                                ? colors.text.primary
                                : colors.text.muted,
                            boxShadow: overflowActive
                                ? "inset 0 1px 0 rgba(255,255,255,0.045), 0 4px 14px rgba(0,0,0,0.16)"
                                : "none",
                            transition:
                                "background-color 120ms ease-out, border-color 120ms ease-out, color 110ms ease-out, box-shadow 120ms ease-out",
                            "&:hover": {
                                background: "rgba(255,255,255,0.032)",
                                color: colors.text.primary,
                            },
                            "&:focus-visible": {
                                borderColor: alpha(colors.accent.soft, 0.52),
                                boxShadow: `0 0 0 3px ${alpha(colors.primary.main, 0.12)}`,
                            },
                        }}
                    >
                        <MoreVert sx={{ fontSize: "1.25rem" }} />
                    </IconButton>
                ) : null}

                {menuOpen ? (
                <Menu
                    id="profile-tab-overflow-menu"
                    anchorEl={menuAnchor}
                    open={menuOpen}
                    onClose={handleCloseMenu}
                    disableScrollLock
                    getContentAnchorEl={null}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                    }}
                    MenuListProps={{
                        dense: true,
                        sx: {
                            py: 0,
                        },
                    }}
                    slotProps={{
                        paper: {
                            sx: {
                                mt: "0.35rem",
                                backdropFilter: "blur(18px)",
                                WebkitBackdropFilter: "blur(18px)",
                                background: "rgba(18,20,22,0.88)",
                                minWidth: "8.5rem",
                                maxWidth: "11rem",
                                border: "1px solid rgba(255,190,70,0.12)",
                                borderRadius: "10px",
                                boxShadow:
                                    "0 10px 28px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.05)",
                                padding: "0.28rem",
                                overflow: "hidden",
                            },
                        },
                    }}
                    sx={{ zIndex: 9999, padding: 0 }}
                >
                    {overflowTabs.map((tab, index) => {
                        const selected = value === tab.id;
                        return (
                            <MenuItem
                                key={tab.id}
                                selected={selected}
                                onClick={() => handleOverflowSelect(tab.id)}
                                dense
                                sx={{
                                    position: "relative",
                                    width: "100%",
                                    justifyContent: "flex-start",
                                    minHeight: "2.15rem",
                                    py: "0.45rem",
                                    px: "0.7rem",
                                    borderRadius: "6px",
                                    borderLeft: "2px solid transparent",
                                    fontSize: "0.7rem",
                                    fontWeight: 600,
                                    letterSpacing: "0.06em",
                                    textTransform: "uppercase",
                                    color: selected
                                        ? colors.primary.main
                                        : colors.text.primary,
                                    backgroundColor: selected
                                        ? alpha(colors.primary.main, 0.1)
                                        : "transparent",
                                    borderLeftColor: selected
                                        ? colors.primary.main
                                        : "transparent",
                                    mt: index > 0 ? "0.18rem" : 0,
                                    "&:hover": {
                                        backgroundColor: alpha(
                                            colors.primary.main,
                                            0.1
                                        ),
                                        borderLeftColor: colors.primary.main,
                                    },
                                    "&.Mui-selected": {
                                        backgroundColor: alpha(
                                            colors.primary.main,
                                            0.1
                                        ),
                                    },
                                    "&.Mui-selected:hover": {
                                        backgroundColor: alpha(
                                            colors.primary.main,
                                            0.14
                                        ),
                                    },
                                }}
                            >
                                {tab.label}
                            </MenuItem>
                        );
                    })}
                </Menu>
                ) : null}
            </Box>
        </Box>
    );
}

ProfileTabBar.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func,
    tabs: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
        })
    ),
};
